import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, useTheme, useMediaQuery } from "@mui/material";
import { keyframes } from "@mui/system";
import TextBox from "../textbox";

interface CountdownTimerProps {
  /** Duration in seconds (default: 30) */
  duration?: number;
  /** Whether the question/step is answered */
  isAnswered: boolean;
  /** Message to show when timer expires */
  notificationMessage?: string;
  /** Custom message to display next to timer */
  bannerMessage?: string;
  /** Show/hide the timer display */
  showTimer?: boolean;
  /** Custom styling for timer text */
  timerSx?: object;
  /** Custom styling for the container */
  containerSx?: object;
  /** Callback when timer reaches zero */
  onTimeUp?: () => void;
}

// Keyframe animations
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
`;

const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-2px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(2px);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  duration = 30,
  isAnswered,
  notificationMessage = "Time's up! Please fill out this question to continue.",
  bannerMessage = "Submit fast, to receive special attention to your case",
  showTimer = true,
  timerSx = {},
  containerSx = {},
  onTimeUp,
}) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    if (isAnswered) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          setShowNotification(true);
          onTimeUp?.();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isAnswered, onTimeUp]);

  useEffect(() => {
    if (isAnswered) {
      setShowNotification(false);
      setTimeLeft(duration);
    }
  }, [isAnswered, duration]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getTimerStatus = () => {
    const oneThird = duration / 3;
    const twoThirds = (duration * 2) / 3;

    if (timeLeft <= oneThird) return "critical";
    if (timeLeft <= twoThirds) return "warning";
    return "normal";
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (isAnswered) return null;

  return (
    <Box sx={{ ...containerSx }}>
      {showTimer && !showNotification && (
        <Paper
          elevation={2}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 1, sm: 2 },
            mb: 1,
            borderRadius: 3,
            background: "transparent",
            animation:
              getTimerStatus() === "critical"
                ? `${pulse} 2s ease-in-out infinite`
                : "none",
          }}
        >
          {/* Left side - Message */}

          <Typography
            variant="h6"
            sx={{
              color: "#495057",
              fontWeight: 600,
              fontSize: { xs: "0.7rem", sm: "1.2rem" },
              lineHeight: 1.4,
              flex: 1,
              mr: 2,
            }}
          >
            {/* {bannerMessage.replace("time", `${formatTime(timeLeft)}`)} */}
            {bannerMessage}
          </Typography>

          {/* Right side - Timer display */}
          <Box
            sx={{
              backgroundColor: "#2c3e50",
              color: "white",
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              minWidth: { xs: "80px", sm: "100px" },
              textAlign: "center",
              animation:
                getTimerStatus() === "critical"
                  ? `${shake} 0.8s ease-in-out infinite`
                  : "none",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "monospace",
                fontWeight: "bold",
                fontSize: { xs: "0.9rem", sm: "1.2rem" },
                letterSpacing: "0.1em",
                ...timerSx,
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        </Paper>
      )}

      {showNotification && <TextBox />}
    </Box>
  );
};

export default CountdownTimer;
